import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TOOLS, DASHBOARD_ITEM, PEOPLE_ITEM, INTEGRATIONS_ITEM, ROLE_LABELS } from "@/config/tools";
import { LogOut, Menu, X, Building2, PanelLeftClose, PanelLeftOpen, Eye, PenLine } from "lucide-react";
import { AssistantWidget } from "@/components/AssistantWidget";
import { AccountSignatureDialog } from "@/components/AccountSignatureDialog";

export default function AppLayout({ children }) {
  const { user, logout, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cc_sidebar_collapsed") === "1");

  const visibleTools = TOOLS.filter((t) => user.tools.includes(t.key));
  const toolsWithPeople = [];
  visibleTools.forEach((t) => {
    toolsWithPeople.push(t);
    if (t.key === "training") toolsWithPeople.push(PEOPLE_ITEM);
  });
  if (!visibleTools.some((t) => t.key === "training")) toolsWithPeople.push(PEOPLE_ITEM);
  const navItems = [DASHBOARD_ITEM, ...toolsWithPeople];
  if (user.role === "admin") navItems.push(INTEGRATIONS_ITEM);

  const initials = (user.name || user.email).split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const headshot = user.headshot ? (user.headshot.startsWith("http") ? user.headshot : `${backend}${user.headshot}`) : "";
  const handleLogout = () => { logout(); navigate("/login"); };
  const handleStopImpersonation = async () => { await stopImpersonation(); navigate("/"); };
  const toggleCollapsed = () => setCollapsed((v) => { localStorage.setItem("cc_sidebar_collapsed", v ? "0" : "1"); return !v; });

  const SidebarInner = ({ mini }) => (
    <>
      <div className={`py-6 border-b border-[#2E4C94] ${mini ? "px-3" : "px-6"}`}>
        <div className={`flex items-center gap-3 ${mini ? "justify-center" : ""}`}>
          <div className="w-10 h-10 rounded-md bg-[#F7941D] flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          {!mini && (
            <div>
              <div className="font-serif text-lg leading-none text-white">MattC Team</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A9B6D9] mt-1">Command Center</div>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.key}
              to={item.path}
              data-testid={`nav-${item.key}`}
              title={mini ? item.name : undefined}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 py-3 rounded-md text-sm font-medium transition-colors duration-200 ${mini ? "px-0 justify-center" : "px-4"} ${
                active ? "bg-[#F7941D] text-white" : "text-[#DBE2F5] hover:bg-[#152A61] hover:text-white"
              }`}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              {!mini && item.name}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-[#2E4C94]">
        <div className={`flex items-center gap-3 py-2 ${mini ? "justify-center" : "px-2"}`}>
          <div className="w-9 h-9 rounded-full bg-[#F7941D] text-white flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">{headshot ? <img src={headshot} alt="" className="w-full h-full object-cover" /> : initials}</div>
          {!mini && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user.name || user.email}</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-[#A9B6D9]">{ROLE_LABELS[user.role]}</div>
            </div>
          )}
        </div>
        <button
          data-testid="account-signature-button"
          onClick={() => setShowSig(true)}
          title={mini ? "Email signature" : undefined}
          className={`mt-2 w-full flex items-center gap-2 py-2 rounded-md text-sm text-[#DBE2F5] hover:bg-[#152A61] hover:text-white transition-colors duration-200 ${mini ? "justify-center px-0" : "px-3"}`}
        >
          <PenLine className="w-4 h-4 shrink-0" strokeWidth={1.5} /> {!mini && "Email signature"}
        </button>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title={mini ? "Sign out" : undefined}
          className={`mt-2 w-full flex items-center gap-2 py-2 rounded-md text-sm text-[#DBE2F5] hover:bg-[#152A61] hover:text-white transition-colors duration-200 ${mini ? "justify-center px-0" : "px-3"}`}
        >
          <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.5} /> {!mini && "Sign out"}
        </button>
      </div>
    </>
  );

  const asideW = collapsed ? "w-20" : "w-64";
  const mainML = collapsed ? "lg:ml-20" : "lg:ml-64";

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex">
      <aside className={`hidden lg:flex flex-col ${asideW} bg-[#1F3C88] fixed inset-y-0 left-0 z-30 transition-[width] duration-300`}>
        <SidebarInner mini={collapsed} />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-[#1F3C88]">
            <SidebarInner mini={false} />
          </aside>
        </div>
      )}

      <div className={`flex-1 ${mainML} flex flex-col min-w-0 transition-[margin] duration-300`}>
        {user.impersonator && (
          <div data-testid="impersonation-banner" className="bg-[#F7941D] text-white px-4 lg:px-8 py-2.5 flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-2 min-w-0">
              <Eye className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">Viewing as <strong>{user.name || user.email}</strong> · impersonated by {user.impersonator.name || user.impersonator.email}</span>
            </span>
            <button data-testid="exit-impersonation-button" onClick={handleStopImpersonation} className="shrink-0 inline-flex items-center gap-1.5 bg-white/25 hover:bg-white/40 px-3 py-1 rounded-full font-semibold transition-colors duration-200">
              <X className="w-3.5 h-3.5" /> Exit
            </button>
          </div>
        )}
        <header className="h-16 bg-white border-b border-[#E2E6F0] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-button" className="lg:hidden text-[#1F3C88]" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <button data-testid="sidebar-toggle-button" onClick={toggleCollapsed} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} className="hidden lg:flex text-[#1F3C88] hover:text-[#F7941D] transition-colors duration-200">
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <div className="hidden lg:block text-xs tracking-[0.2em] uppercase text-[#555]">Residential Real Estate Operations</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-[#1F3C88] text-white">{ROLE_LABELS[user.role]}</span>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 lg:p-10">{children}</main>
      </div>
      <AssistantWidget />
      <AccountSignatureDialog open={showSig} onOpenChange={setShowSig} />
    </div>
  );
}
